import { useSyncExternalStore } from "react";
import { pushWorldPref } from "../prefs.js";
import {
  formatHours,
  hoursFor,
  hoursForSetting,
  parseTimeIntent,
  parseTimeSetting,
} from "./time.js";
import type { TimeSetting } from "./time.js";

/**
 * The user's choice of time of day for the world. `?tod=` seeds it (screenshots, tests); otherwise
 * the device cache, and the server's `world.timeOfDay` once the prefs arrive. Every sky consumer
 * reads it through `useSun`, so one change repaints sun, sky, reflections, windows and lamps.
 */
export const TIME_STORAGE_KEY = "uz.world.timeOfDay";

function fromQuery(): TimeSetting | null {
  const search = globalThis.location?.search ?? "";
  return /[?&]tod=/.test(search) ? hoursFor(search) : null;
}

function readCache(): TimeSetting | null {
  try {
    const raw = globalThis.localStorage.getItem(TIME_STORAGE_KEY);
    if (raw === null) return null;
    return parseTimeSetting(raw === "auto" ? "auto" : Number(raw));
  } catch {
    return null;
  }
}

function writeCache(setting: TimeSetting): void {
  try {
    globalThis.localStorage.setItem(TIME_STORAGE_KEY, String(setting));
  } catch {
    // Not cached without storage; the server copy still follows the user.
  }
}

/** Set when the URL fixed the hour: a server value must not override it on load. */
let pinnedByUrl = false;
let setting: TimeSetting = "auto";
const listeners = new Set<() => void>();

export function initTimeSetting(): void {
  const q = fromQuery();
  pinnedByUrl = q !== null;
  setting = q ?? readCache() ?? "auto";
}
initTimeSetting();

export function getTimeSetting(): TimeSetting {
  return setting;
}

export function subscribeTime(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(next: TimeSetting): void {
  if (next === setting) return;
  setting = next;
  for (const l of listeners) l();
}

/** The user (HUD, assistant command) picks a time: applied now, cached, saved to the account. */
export function setTimeSetting(next: TimeSetting): void {
  pinnedByUrl = false;
  emit(next);
  writeCache(next);
  pushWorldPref({ timeOfDay: next });
}

/** A value read from the server: applied and cached, never echoed back (and `?tod=` wins). */
export function adoptTimeSetting(next: TimeSetting | undefined): void {
  if (next === undefined || pinnedByUrl) return;
  emit(next);
  writeCache(next);
}

/** The setting as an hour right now. */
export function currentHours(): number {
  return hoursForSetting(setting);
}

export function useTimeSetting(): TimeSetting {
  return useSyncExternalStore(subscribeTime, getTimeSetting, () => "auto");
}

/** For `syncWorldPrefs`: what this device has cached, to push up when the server has none. */
export function cachedTimeSetting(): TimeSetting | undefined {
  return pinnedByUrl ? undefined : (readCache() ?? undefined);
}

/** A slider drag: repaints the sky now, saves nothing until `setTimeSetting` commits. */
export function previewTimeSetting(next: TimeSetting): void {
  emit(next);
}

/**
 * A typed or spoken command ("set the world to night"): applies it and returns the new setting,
 * or null when the text is something else. Client side, so it works without the server.
 */
export function applyTimeCommand(text: string): TimeSetting | null {
  const next = parseTimeIntent(text);
  if (next !== null) setTimeSetting(next);
  return next;
}

/** The world-namespace toast key and params that confirm a time command. */
export function timeToast(next: TimeSetting): {
  key: string;
  params: Record<string, string>;
} {
  return next === "auto"
    ? { key: "time.setAuto", params: {} }
    : { key: "time.set", params: { when: formatHours(next) } };
}
